/**
 * Preauthorization Completed Dialog
 * For marking PoC as preauthorization completed (submitted -> approved)
 */

import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Grid,
  CircularProgress,
  Stack,
  alpha,
} from '@mui/material'
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

interface PreauthCompletedDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    pre_auth_id: string
    approval_date: string
    authorized_no_session: number
    auth_service_period: string
  }) => Promise<void>
  loading?: boolean
}

const PreauthCompletedDialog: React.FC<PreauthCompletedDialogProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [preAuthId, setPreAuthId] = useState('')
  const [approvalDate, setApprovalDate] = useState<Date | null>(new Date())
  const [authorizedSessions, setAuthorizedSessions] = useState('')
  const [servicePeriod, setServicePeriod] = useState<Date | null>(null)

  const handleSubmit = async () => {
    if (!preAuthId || !approvalDate || !authorizedSessions || !servicePeriod) {
      return
    }
    // Convert dates to ISO string format
    const isoApprovalDate = approvalDate.toISOString()
    const isoServicePeriod = servicePeriod.toISOString()
    await onSubmit({
      pre_auth_id: preAuthId,
      approval_date: isoApprovalDate,
      authorized_no_session: parseInt(authorizedSessions, 10),
      auth_service_period: isoServicePeriod,
    })
  }

  const handleClose = () => {
    if (!loading) {
      setPreAuthId('')
      setApprovalDate(new Date())
      setAuthorizedSessions('')
      setServicePeriod(null)
      onClose()
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2.5,
            pt: 3,
            px: 3,
            background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: alpha('#fff', 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              Preauthorization Completed
            </Typography>
          </Stack>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={loading}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: alpha('#fff', 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            py: '2rem !important',
            px: '2.5rem !important',
            pr: '4.5rem !important',
          }}
        >
          <Stack spacing={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500, mb: 0 }}
            >
              Enter the preauthorization completion details
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Pre Authorization ID"
                  value={preAuthId}
                  onChange={e => setPreAuthId(e.target.value)}
                  disabled={loading}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#4caf50',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4caf50',
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Approval Date"
                  value={approvalDate}
                  onChange={newValue => {
                    // Convert PickerValue (Date | Dayjs) to Date
                    const dateObj =
                      newValue instanceof Date
                        ? newValue
                        : (newValue as any)?.toDate
                          ? (newValue as any).toDate()
                          : newValue
                            ? new Date(newValue as any)
                            : null
                    setApprovalDate(dateObj)
                  }}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#4caf50',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4caf50',
                            borderWidth: 2,
                          },
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Authorized Number of Sessions"
                  type="number"
                  value={authorizedSessions}
                  onChange={e => {
                    const val = e.target.value
                    if (val === '') {
                      setAuthorizedSessions('')
                      return
                    }
                    const num = parseInt(val, 10)
                    if (Number.isNaN(num)) return
                    const clamped = Math.min(30, Math.max(1, num))
                    setAuthorizedSessions(String(clamped))
                  }}
                  disabled={loading}
                  variant="outlined"
                  inputProps={{ min: 1, max: 30, step: 1 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#4caf50',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4caf50',
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  label="Authorized Service Period (Valid Until)"
                  value={servicePeriod}
                  onChange={newValue => {
                    // Convert PickerValue (Date | Dayjs) to Date
                    const dateObj =
                      newValue instanceof Date
                        ? newValue
                        : (newValue as any)?.toDate
                          ? (newValue as any).toDate()
                          : newValue
                            ? new Date(newValue as any)
                            : null
                    setServicePeriod(dateObj)
                  }}
                  disabled={loading}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#4caf50',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#4caf50',
                            borderWidth: 2,
                          },
                        },
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50',
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: alpha('#000', 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              loading ||
              !preAuthId ||
              !approvalDate ||
              !authorizedSessions ||
              !servicePeriod
            }
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              backgroundColor: '#4caf50',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: '#388e3c',
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
              },
              '&:disabled': {
                backgroundColor: alpha('#4caf50', 0.5),
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default PreauthCompletedDialog
